// ADR-057 — translates the engineer-facing `OrganizationType` enum into treasurer-first display
// labels at the render boundary (the contact-form organization-subtype picker). Sibling of
// `expenditureCategoryLabel`: a pure, React-free map so it can be reused across packages and
// snapshot-tested cheaply. Per `feedback_treasurer_first_copy.md`, the compound PascalCase
// identifiers (`CandidateCommittee`, `OtherCommittee`, `OtherOrganization`) must never render
// verbatim. OrganizationType is the single organization-subtype axis that replaced the retired
// federal-only committee-kind field.

import { OrganizationType } from '../generated/enums.gen';

/**
 * Per-subtype treasurer-first label. Exported so the test can pin the literals; a new
 * `OrganizationType` member without an entry surfaces as a compile error (the `Record` is
 * keyed by the enum).
 */
export const ORGANIZATION_TYPE_LABELS: Readonly<Record<OrganizationType, string>> = {
  [OrganizationType.Corporation]: 'Corporation',
  // "PAC" already reads as plain English and is how treasurers refer to it.
  [OrganizationType.Pac]: 'PAC',
  [OrganizationType.Party]: 'Party committee',
  [OrganizationType.CandidateCommittee]: 'Candidate committee',
  [OrganizationType.OtherCommittee]: 'Other political committee',
  [OrganizationType.OtherOrganization]: 'Other organization',
  [OrganizationType.Union]: 'Labor union',
};

/**
 * Resolves the treasurer-first display label for an `OrganizationType`. Falls back to the raw
 * value for any unexpected (non-enum) input so the boundary never renders empty.
 */
export function organizationTypeLabel(type: OrganizationType): string {
  return ORGANIZATION_TYPE_LABELS[type] ?? type;
}
